// mds-dojo — dojo/dom-attr. set/get/has/remove with dojo's property-vs-attribute
// semantics.
//
// CRITICAL (the bug this file exists to get right): for live form/DOM state —
// `value`, `checked`, `selected`, `disabled`, … — dojo reads/writes the DOM
// *property* (`node.value`), NOT the HTML *attribute* (`node.getAttribute("value")`).
// getAttribute("value") returns the INITIAL markup value, so after a user types
// into an <input> it's stale/empty. Reading these as attributes silently returns
// the wrong value app-wide (empty login username/password, unchanged checkboxes…).
// dojo maintains a "force property" list for exactly these; we mirror it.

// lower-cased incoming name -> the real DOM property name (live state).
const FORCE_PROP: Record<string, string> = {
  value: 'value',
  checked: 'checked',
  selected: 'selected',
  disabled: 'disabled',
  readonly: 'readOnly',
  required: 'required',
  multiple: 'multiple',
  indeterminate: 'indeterminate',
  tabindex: 'tabIndex',
  classname: 'className',
  innerhtml: 'innerHTML',
  textcontent: 'textContent',
  htmlfor: 'htmlFor',
};
// which FORCE_PROP entries are string-valued (assign as-is); the rest are boolean DOM state.
const STRING_PROP: Record<string, 1> = { value: 1, className: 1, innerHTML: 1, textContent: 1, htmlFor: 1, tabIndex: 1 };

const NAME_MAP: Record<string, string> = { className: 'class', htmlFor: 'for' };

function setOne(node: any, name: string, value: any): void {
  if (!node) { return; }
  const lname = String(name).toLowerCase();

  if (name === 'style') {
    if (value && typeof value === 'object') {
      for (const k in value) { node.style[k] = value[k]; }
    } else {
      node.style.cssText = value == null ? '' : String(value);
    }
    return;
  }
  if (lname === 'class') { node.className = value == null ? '' : value; return; }
  if (/^on/i.test(name) && typeof value === 'function') { node[name.toLowerCase()] = value; return; }

  const prop = FORCE_PROP[lname];
  if (prop) {
    if (STRING_PROP[prop]) {
      // live string property (value/className/innerHTML/…). NEVER setAttribute —
      // that touches the static markup, not what the field displays/submits.
      node[prop] = value == null ? '' : value;
    } else {
      // boolean DOM state (checked/disabled/selected/…). Accept dojo's loose inputs
      // ("disabled", "", true, false) → coerce to boolean; reflect the attribute so
      // CSS / [attr] selectors still match.
      const b = value === '' ? false : (typeof value === 'string' ? value !== 'false' : !!value);
      node[prop] = b;
      if (b) { node.setAttribute(lname, lname); } else { node.removeAttribute(lname); }
    }
    return;
  }

  if (typeof value === 'boolean') {
    node[name] = value;
    if (value) { node.setAttribute(name, name); } else { node.removeAttribute(name); }
    return;
  }
  if (value == null) { node.removeAttribute(NAME_MAP[name] || name); return; }
  node.setAttribute(NAME_MAP[name] || name, value);
}

export function set(node: any, name: string | Record<string, any>, value?: any): any {
  if (typeof name === 'object') {
    for (const k in name) { setOne(node, k, (name as any)[k]); }
    return node;
  }
  setOne(node, name, value);
  return node;
}

export function get(node: any, name: string): any {
  if (!node) { return null; }
  const lname = String(name).toLowerCase();
  if (lname === 'class') { return node.className; }

  const prop = FORCE_PROP[lname];
  if (prop) { return node[prop]; }        // live property (value/checked/…), not the static attr

  // dojo: boolean/function-valued reflected props come off the property, not the attr.
  const v = node[name];
  if (typeof v === 'boolean' || typeof v === 'function') { return v; }

  return (node.hasAttribute && node.hasAttribute(name)) ? node.getAttribute(name) : null;
}

export function has(node: any, name: string): boolean {
  if (!node) { return false; }
  const lname = String(name).toLowerCase();
  if (FORCE_PROP[lname]) { return (FORCE_PROP[lname] in node); }
  return !!(node.hasAttribute && node.hasAttribute(name));
}

export function remove(node: any, name: string): void {
  if (node && node.removeAttribute) { node.removeAttribute(NAME_MAP[name] || name); }
}

const domAttr = { set, get, has, remove };
export default domAttr;
